import { Injectable, signal } from '@angular/core';

// Type definitions for Direct Sockets API (Experimental)
interface UDPSocketOptions {
  localAddress?: string;
  localPort?: number;
  remoteAddress?: string;
  remotePort?: number;
}

interface UDPSocket {
  close(): Promise<void>;
  readable: ReadableStream<UDPMessage>;
  writable: WritableStream<UDPMessage>;
  joinMulticastGroup(address: string): Promise<void>;
  leaveMulticastGroup(address: string): Promise<void>;
}

interface UDPMessage {
  data: BufferSource;
  remoteAddress: string;
  remotePort: number;
}

declare global {
  interface Window {
    UDPSocket?: {
      new (options?: UDPSocketOptions): UDPSocket;
    };
  }
}

export interface DiscoveredPrinter {
  address: string;
  port: number;
  name?: string;
  protocol: 'mdns' | 'ssdp' | 'unknown';
  lastSeen: Date;
}

export interface PrinterDiscoverySettings {
  autoConnect: boolean;
  preferredProtocol: 'mdns' | 'ssdp';
}

@Injectable({
  providedIn: 'root',
})
export class PrinterDiscoveryService {
  readonly isSupported = signal<boolean>(false);
  readonly isScanning = signal<boolean>(false);
  readonly discoveredPrinters = signal<DiscoveredPrinter[]>([]);
  readonly connectedPrinter = signal<DiscoveredPrinter | null>(null);
  readonly isConnecting = signal<boolean>(false);
  readonly lastError = signal<string | null>(null);
  readonly isInitialized = signal<boolean>(false);

  private socket: UDPSocket | null = null;
  private reader: ReadableStreamDefaultReader<UDPMessage> | null = null;
  private abortController: AbortController | null = null;
  private currentSettings: PrinterDiscoverySettings = {
    autoConnect: true,
    preferredProtocol: 'mdns',
  };

  // Common multicast groups for discovery
  private readonly MDNS_GROUP = '224.0.0.251';
  private readonly MDNS_PORT = 5353;

  private readonly SSDP_GROUP = '239.255.255.250';
  private readonly SSDP_PORT = 1900;

  constructor() {
    this.checkSupport();
  }

  private checkSupport() {
    if ('UDPSocket' in window) {
      this.isSupported.set(true);
    }
  }

  /**
   * Update service settings
   */
  updateSettings(settings: Partial<PrinterDiscoverySettings>): void {
    this.currentSettings = { ...this.currentSettings, ...settings };
  }

  /**
   * Get current settings
   */
  getSettings(): PrinterDiscoverySettings {
    return { ...this.currentSettings };
  }

  async connect(printer: DiscoveredPrinter) {
    if (this.isConnecting()) return;

    this.isConnecting.set(true);
    try {
      // Simulate connection handshake
      await new Promise((resolve) => setTimeout(resolve, 1500));
      this.connectedPrinter.set(printer);
    } catch (error) {
      this.handleError('Failed to connect to printer', error);
    } finally {
      this.isConnecting.set(false);
    }
  }

  async disconnect() {
    this.connectedPrinter.set(null);
  }

  async printTestPage() {
    const printer = this.connectedPrinter();
    if (!printer) {
      throw new Error('No printer connected');
    }

    console.log(`Sending test page to ${printer.address}:${printer.port}`);
    // Here we would open a TCP socket to the printer and send ESC/POS commands
    // For now, we just simulate success
    await new Promise((resolve) => setTimeout(resolve, 500));
  }

  /**
   * Start scanning for printers using Multicast DNS (mDNS)
   * This requires the Direct Sockets API with multicast support (Chrome 144+)
   */
  async startScan() {
    if (!this.isSupported()) {
      this.handleError('Direct Sockets API not supported');
      return;
    }

    if (this.isScanning()) {
      return;
    }

    this.isScanning.set(true);
    this.lastError.set(null);
    this.abortController = new AbortController();

    try {
      // Create UDP socket bound to mDNS port
      // Note: In a real app, you might need to bind to 0 (random) and send queries
      // instead of listening on 5353 which might be taken by the OS.
      // For discovery, we often send a query to the multicast group.

      // @ts-ignore - Experimental API
      this.socket = new window.UDPSocket({
        localAddress: '0.0.0.0',
        localPort: 0, // Let OS choose a port
      });

      // Join mDNS multicast group
      // This is the new feature in Chrome 144
      try {
        await this.socket!.joinMulticastGroup(this.MDNS_GROUP);
        console.log(`Joined multicast group ${this.MDNS_GROUP}`);
      } catch (e) {
        console.warn('Multicast join failed (feature might not be enabled):', e);
        // Continue anyway, maybe we can still send/receive unicast
      }

      // Start reading loop
      this.readLoop();

      // Send a discovery query (mDNS query for _ipp._tcp.local or _printer._tcp.local)
      await this.sendDiscoveryQuery();
    } catch (error) {
      this.handleError('Failed to start printer scan', error);
      this.stopScan();
    }
  }

  async stopScan() {
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }

    if (this.reader) {
      try {
        await this.reader.cancel();
      } catch (e) {
        // Ignore cancel errors
      }
      this.reader = null;
    }

    if (this.socket) {
      try {
        await this.socket.close();
      } catch (e) {
        console.error('Error closing socket:', e);
      }
      this.socket = null;
    }

    this.isScanning.set(false);
  }

  /**
   * Reset the service state (clear discovered printers, errors, etc.)
   */
  reset(): void {
    this.discoveredPrinters.set([]);
    this.lastError.set(null);
    this.isInitialized.set(false);
  }

  private async readLoop() {
    if (!this.socket) return;

    try {
      this.reader = this.socket.readable.getReader();

      while (true) {
        const { value, done } = await this.reader.read();
        if (done) break;

        if (value) {
          this.processMessage(value);
        }
      }
    } catch (error) {
      if (this.isScanning()) {
        console.error('Socket read error:', error);
      }
    }
  }

  private processMessage(msg: UDPMessage) {
    // Basic parsing logic - in a real app, use a proper DNS packet parser
    // For now, we just log that we found something and add it to the list

    const existing = this.discoveredPrinters().find((p) => p.address === msg.remoteAddress && p.port === msg.remotePort);

    if (!existing) {
      const newPrinter: DiscoveredPrinter = {
        address: msg.remoteAddress,
        port: msg.remotePort,
        protocol: 'mdns', // Assuming mDNS for now
        lastSeen: new Date(),
      };

      this.discoveredPrinters.update((list) => [...list, newPrinter]);
      console.log('Found printer:', newPrinter);
    } else {
      // Update last seen
      this.discoveredPrinters.update((list) => list.map((p) => (p === existing ? { ...p, lastSeen: new Date() } : p)));
    }
  }

  private async sendDiscoveryQuery() {
    if (!this.socket) return;

    // Construct a basic mDNS query packet for _ipp._tcp.local
    // This is a simplified binary payload for demonstration
    // Transaction ID: 0x0000
    // Flags: 0x0000 (Standard Query)
    // Questions: 1
    // Answer RRs: 0
    // Authority RRs: 0
    // Additional RRs: 0
    // Query: _ipp._tcp.local

    const query = new Uint8Array([
      0x00,
      0x00, // ID
      0x00,
      0x00, // Flags
      0x00,
      0x01, // Questions
      0x00,
      0x00, // Answers
      0x00,
      0x00, // Authority
      0x00,
      0x00, // Additional
      // _ipp._tcp.local
      0x04,
      0x5f,
      0x69,
      0x70,
      0x70, // ._ipp
      0x04,
      0x5f,
      0x74,
      0x63,
      0x70, // ._tcp
      0x05,
      0x6c,
      0x6f,
      0x63,
      0x61,
      0x6c, // .local
      0x00, // End of name
      0x00,
      0x0c, // Type: PTR
      0x00,
      0x01, // Class: IN
    ]);

    try {
      const writer = this.socket.writable.getWriter();
      await writer.write({
        data: query,
        remoteAddress: this.MDNS_GROUP,
        remotePort: this.MDNS_PORT,
      });
      writer.releaseLock();
    } catch (error) {
      console.error('Failed to send discovery query:', error);
    }
  }

  private handleError(message: string, error: any = null) {
    const errorMessage = error ? `${message}: ${error.message || error}` : message;
    console.error(errorMessage);
    this.lastError.set(errorMessage);
  }
}
